import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StaffOption { id: string; name: string }

const useStaffOptions = () => {
  const { user } = useAuth();
  return useQuery<StaffOption[]>({
    queryKey: ['shift-report-staff-options'],
    enabled: !!user,
    queryFn: async () => {
      try {
        const { data, error } = await supabase.functions.invoke('list-users');
        if (error) throw error;
        const users = (data?.users || []) as any[];
        return users
          .map((u) => ({ id: u.id as string, name: (u.full_name || u.email || 'Unknown') as string }))
          .filter((u) => !!u.id)
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch {
        // fallback: profiles (own only via RLS, harmless)
        const { data } = await supabase.from('profiles').select('id, full_name');
        return (data || []).map((p: any) => ({ id: p.id, name: p.full_name || 'Unknown' }));
      }
    },
    staleTime: 5 * 60 * 1000,
  });
};

interface Props {
  value: string;
  onChange: (v: string) => void;
  multi?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
}

export const StaffSelectField: React.FC<Props> = ({ value, onChange, multi, placeholder, readOnly, className }) => {
  const { data: options = [], isLoading } = useStaffOptions();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedNames = useMemo(
    () => value ? value.split(',').map((s) => s.trim()).filter(Boolean) : [],
    [value]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
  }, [options, search]);

  const toggle = (name: string) => {
    if (!multi) {
      onChange(name);
      setOpen(false);
      return;
    }
    const set = new Set(selectedNames);
    if (set.has(name)) set.delete(name); else set.add(name);
    onChange(Array.from(set).join(', '));
  };

  const remove = (name: string) => {
    onChange(selectedNames.filter((n) => n !== name).join(', '));
  };

  return (
    <Popover open={open && !readOnly} onOpenChange={(o) => !readOnly && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={readOnly}
          className={cn(
            'w-full justify-between font-normal h-auto min-h-10 py-2',
            !selectedNames.length && 'text-muted-foreground',
            className,
          )}
        >
          <div className="flex flex-wrap gap-1 text-left flex-1">
            {selectedNames.length === 0 && (placeholder || (multi ? 'Select staff' : 'Select staff member'))}
            {multi
              ? selectedNames.map((n) => (
                  <Badge key={n} variant="secondary" className="gap-1 font-normal">
                    {n}
                    {!readOnly && (
                      <span
                        role="button"
                        onClick={(e) => { e.stopPropagation(); remove(n); }}
                        className="hover:text-destructive cursor-pointer"
                      >
                        <X className="h-3 w-3" />
                      </span>
                    )}
                  </Badge>
                ))
              : <span>{selectedNames[0]}</span>}
          </div>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search staff..."
            className="h-8"
          />
        </div>
        <ScrollArea className="max-h-64">
          <div className="p-1">
            {isLoading && <div className="px-2 py-3 text-xs text-muted-foreground">Loading…</div>}
            {!isLoading && filtered.length === 0 && (
              <div className="px-2 py-3 text-xs text-muted-foreground">No staff found</div>
            )}
            {filtered.map((o) => {
              const checked = selectedNames.includes(o.name);
              return (
                <div
                  key={o.id}
                  onClick={() => toggle(o.name)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm"
                >
                  {multi
                    ? <Checkbox checked={checked} className="pointer-events-none" />
                    : <span className={cn('h-2 w-2 rounded-full', checked ? 'bg-primary' : 'bg-transparent')} />}
                  <span>{o.name}</span>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
